import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletProvider';
import Loader from '../components/Loader';
import { fetchUser, fetchActivities } from '../services/zen';
import { getIncomeLog } from '../services/api';
import { money, fmtDate } from '../utils/format';

const ACT_LABEL = { 0: 'Registered', 1: 'Claimed' };

export default function Reports() {
  const { provider, address, isCorrectNetwork } = useWallet();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    if (!provider || !address || !isCorrectNetwork) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true);
        const u = await fetchUser(provider, address);
        if (!alive) return;
        setUser(u);
        if (u.registered) {
          // on-chain activities (register/claim) + level income (from DB log)
          const [acts, income] = await Promise.all([
            fetchActivities(provider, u.id, 50).catch(() => []),
            getIncomeLog(u.id, 80).catch(() => []),
          ]);
          const merged = [
            ...acts.map((a) => ({
              ts: a.timestamp,
              kind: ACT_LABEL[a.type] || 'Activity',
              detail: a.type === 1 ? 'Withdrawn to wallet' : 'Joined Zen Capital',
              amount: a.type === 1 ? a.amount : null,
            })),
            ...income.map((e) => ({
              ts: Math.floor(new Date(e.created_at.replace(' ', 'T')).getTime() / 1000) || 0,
              kind: `Level ${e.level} Income`,
              detail: `From ID ${e.from_user}`,
              amount: ethers.BigNumber.from(e.amount || '0'),
            })),
          ].sort((x, y) => y.ts - x.ts);
          if (alive) setRows(merged);
        }
      } catch (_) {} finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [provider, address, isCorrectNetwork]);

  if (loading) return <Loader messages={['Loading reports', 'Reading your history']} />;

  if (!user || !user.registered) {
    return <div><h1 className="page-title">Reports</h1><div className="card"><p className="muted">Register first to see your reports.</p></div></div>;
  }

  return (
    <div>
      <h1 className="page-title">Reports</h1>

      <div className="stat-grid">
        <div className="stat"><div className="k">Total Earned</div><div className="v gold">{money(user.totalEarned)}</div></div>
        <div className="stat"><div className="k">Claimable Now</div><div className="v up">{money(user.pendingRewards)}</div></div>
      </div>

      <div className="spacer-18" />

      <div className="card">
        <div className="card-title">Activity History</div>
        {rows.length === 0 ? (
          <p className="muted" style={{ fontSize: 12.5 }}>No activity yet. Income and claims will appear here.</p>
        ) : (
          <table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th>Detail</th><th>Amount</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{fmtDate(r.ts)}</td>
                  <td style={{ color: 'var(--blue-bright)' }}>{r.kind}</td>
                  <td style={{ color: 'var(--text-dim)' }}>{r.detail}</td>
                  <td style={{ color: r.amount ? 'var(--gold-bright)' : 'var(--text-faint)' }}>
                    {r.amount ? money(r.amount) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="spacer-12" />
      <p className="muted center" style={{ fontSize: 11 }}>
        Income events are read from the blockchain. Recent entries appear after network confirmation.
      </p>
    </div>
  );
}
